import type { ImageStyle } from "expo-image";
// eslint-disable-next-line no-restricted-imports -- static brand asset; the custom Image is for remote (uri) sources only
import { Image } from "expo-image";
import type { StyleProp } from "react-native";
import { useUnistyles } from "react-native-unistyles";

import type { Device } from "$modules/expo-tv-connect-remote";
import androidtv from "$src/core/assets/brands/androidtv.webp";
import appletv from "$src/core/assets/brands/appletv.webp";
import lg from "$src/core/assets/brands/lg.webp";
import samsung from "$src/core/assets/brands/samsung.webp";

import $ from "./TvBrandLogo.styles";

type Props = {
  device: Device;
  style?: StyleProp<ImageStyle>;
  /** Overrides theme foreground (e.g. on gradient device cards). */
  color?: string;
};

const LOGOS = {
  androidtv: { source: androidtv, style: $.androidtv, label: "Android TV" },
  appletv: { source: appletv, style: $.appletv, label: "Apple TV" },
  lg: { source: lg, style: $.lg, label: "LG" },
  samsung: { source: samsung, style: $.samsung, label: "Samsung" },
} as const;

const getTvBrand = (device: Device): keyof typeof LOGOS | null => {
  switch (device.platform) {
    case "webos":
      return "lg";
    case "tizen":
      return "samsung";
    case "mdns":
      switch (device.txt?.platform) {
        case "tvos":
          return "appletv";
        case "androidtv":
          return "androidtv";
        default:
          return null;
      }
    default:
      return null;
  }
};

const TvBrandLogo = ({ device, style, color }: Props) => {
  const { theme } = useUnistyles();

  const brand = getTvBrand(device);
  if (brand === null) return null;

  const { source, style: logoStyle, label } = LOGOS[brand];

  const logoColor = color ?? theme.colors.foregroundDefault;

  return (
    <Image
      source={source}
      tintColor={logoColor}
      contentFit="contain"
      contentPosition="left"
      accessibilityRole="image"
      accessibilityLabel={label}
      style={[logoStyle, style]}
    />
  );
};

export default TvBrandLogo;
